import UserDetailAPIView from "issara-sdk/apis/UserDetailAPIView_users";

import DivisionHasUserBulkCreate from "issara-sdk/apis/DivisionHasUserBulkCreate_apps_PRXM";
import DivisionHasUserBulkUpdate from "issara-sdk/apis/DivisionHasUserBulkUpdate_apps_PRX";
import DivisionHasUserDetailForOtherUser from "issara-sdk/apis/DivisionHasUserDetailForOtherUser_apps_PRXM";
import DivisionHasUserListForOtherUser from "issara-sdk/apis/DivisionHasUserListForOtherUser_apps_PRX";

import AffiliateSerializer from "issara-sdk/types/AffiliateSerializer_users";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  mapOptions,
  MasterMapData,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";
import { getDivisionParentName } from "react-lib/apps/HISV3/common/sequence/AppInitialize";

import { ADMIN_LABEL_LIST } from "react-lib/apps/USER/CardSettingDivision";

import {
  ADMIN_MENU_LIST,
  AvailableAffiliateParamsType,
  flattenDivisionTree,
  groupDivisionOrganization,
  ORGANIZE_LABEL,
  searchDivisionsAsTree,
} from "../HRMInterface";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  INIT: "INIT",
  ADD: "ADD",
  AFFILIATE_SEARCH: "AFFILIATE_SEARCH",
  CHANGE_DIVISION: "CHANGE_DIVISION",
  CHANGE_TREE_VIEW: "CHANGE_TREE_VIEW",
  CHANGE_VALUE: "CHANGE_VALUE",
  REMOVE: "REMOVE",
  SAVE: "SAVE",
} as const;

export const SEQUENCE_NAME = "DSPermission" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  DSPermissionSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    affiliateDict: Record<string, AffiliateSerializer[]>;
    divisionOrganizations: { flatted: Record<string, any>[]; grouped: Record<string, any>[] };
    patientOpDivisions: PatientOpDivisionType[];
    roles: number[];
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage" | "selectedEmployee">;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type PatientOpDivisionType = {
  adminMenuList: string[];
  divisions: { id: number; active: boolean; dhuId?: number; extra?: Record<string, any> }[];
  is_Active?: boolean;
  isAccessPatient?: boolean;
  parentId?: number | null;
  parentName?: string;
  rowIndex: number;
};

export type AdminMenuKeys = keyof typeof ADMIN_MENU_LIST;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  allDivisionList?: any[];
  device?: number;
  division?: number;
  allDivisionGroup?: any[];
  masterData?: MasterMapData<typeof Masters>;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.AFFILIATE_SEARCH]: AvailableAffiliateParamsType & { id: number };
  [ACTIONS.CHANGE_DIVISION]: { index: number; value: number[] };
  [ACTIONS.CHANGE_TREE_VIEW]: { index: number; value?: string };
  [ACTIONS.CHANGE_VALUE]: { index: number; name: any; value: any };
  [ACTIONS.REMOVE]: { index: number; onSuccess: () => void };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  DSPermissionSequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["role", {}],
  ["division", {}],
  ["prenameEn", {}],
] as const;

export const ADMIN_MENU_OPTIONS = Object.entries(ADMIN_MENU_LIST).map(([key, value]) => {
  const option = Array.isArray(value)
    ? { ...mapOptions([key])[0], subOptions: mapOptions(value) }
    : mapOptions([key])[0];

  const label = ADMIN_LABEL_LIST[key] || key;

  return { ...option, text: label };
});

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    { DSPermissionSequence: { ...state.DSPermissionSequence, sequenceIndex: "Action" } },
    () => {
      Action(controller, { ...params, action: ACTIONS.INIT });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.INIT]: HandleInit,
    [ACTIONS.ADD]: HandleAdd,
    [ACTIONS.AFFILIATE_SEARCH]: HandleParentSearch,
    [ACTIONS.CHANGE_DIVISION]: HandleChangeDivision,
    [ACTIONS.CHANGE_TREE_VIEW]: HandleChangeTreeView,
    [ACTIONS.CHANGE_VALUE]: HandleChangeValue,
    [ACTIONS.REMOVE]: HandleRemove,
    [ACTIONS.SAVE]: HandleSave,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleInit: Handler<Params<Actions["INIT"]>> = async (controller) => {
  let state = controller.getState();

  const userEmployee = state.selectedEmployee?.user_employee?.[0];
  const userId = userEmployee?.id;

  const groupedDivisions = groupDivisionOrganization(controller.data.allDivisionList ?? [], [
    ORGANIZE_LABEL,
  ]);

  const flattedDivision = flattenDivisionTree(groupedDivisions);

  const [[result], groupedItems] = await Promise.all([
    UserDetailAPIView.retrieve({
      apiToken: controller.apiToken,
      pk: userId,
      extra: { division: controller.data.division },
    }),
    GetDivisionHasUserGroupList(controller, { flattedDivision, userId }),
  ]);

  state = controller.getState();

  const affiliate: Record<string, any>[] | undefined = state.selectedEmployee?.affiliate || [];
  const division = affiliate?.[0];
  const parentDiv = affiliate?.[1];

  const foundOrganize = flattedDivision.find((item) => item.id === division?.id);

  const parentName = getDivisionParentName(
    foundOrganize ? division?.id : parentDiv?.id,
    controller.data.allDivisionGroup ?? []
  );

  if (groupedItems.length === 0) {
    const data: PatientOpDivisionType = {
      adminMenuList: [],
      divisions: [],
      is_Active: true,
      isAccessPatient: true,
      rowIndex: 0,
      ...(foundOrganize
        ? { parentId: division?.id, parentName }
        : { divisions: [{ id: division?.id, active: true }], parentId: parentDiv?.id, parentName }),
    };

    groupedItems.push(data);
  }

  const dict = Object.fromEntries(groupedItems.map((item) => [item.rowIndex, groupedDivisions]));

  controller.setState({
    DSPermissionSequence: {
      ...state.DSPermissionSequence,
      affiliateDict: dict,
      divisionOrganizations: { flatted: flattedDivision, grouped: groupedDivisions },
      patientOpDivisions: groupedItems,
      roles: result?.roles || [],
    },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const userEmployee = state.selectedEmployee?.user_employee?.[0];
  const userId = userEmployee?.id;

  const seq = state.DSPermissionSequence ?? {};

  const formattedItems = formatDivisionHasUser(seq.patientOpDivisions ?? [], userId);

  // แยกข้อมูลออกเป็น 3 กลุ่ม: สร้างใหม่, ลบ, และอัพเดท
  const createdItems = formattedItems.filter((item) => !item.dhuId && !item.deleted);
  const deletedItems = formattedItems.filter((item) => item.deleted);
  const updatedItems = [
    ...new Map(
      formattedItems
        .filter((item) => item.dhuId && !item.deleted)
        .map((item) => [item.rowIndex, item])
    ).values(),
  ];

  // อัพเดท roles ของ user
  const [userRes, userErr] = await UserDetailAPIView.patch({
    apiToken: controller.apiToken,
    data: { roles: state.DSPermissionSequence?.roles },
    pk: userId,
    extra: { division: controller.data.division },
  });

  if (userErr) {
    SetErrorMessage(controller, { ...params, error: userErr });

    return;
  }

  // สร้าง division has user records
  if (createdItems.length > 0) {
    const [, error] = await DivisionHasUserBulkCreate.create({
      apiToken: controller.apiToken,
      data: createdItems as any,
    });

    if (error) {
      SetErrorMessage(controller, { ...params, error });

      return;
    }
  }

  // ลบ division has user records
  if (deletedItems.length > 0) {
    const promiseArr = deletedItems.map(
      (item) =>
        DivisionHasUserDetailForOtherUser.delete({
          apiToken: controller.apiToken,
          pk: item.dhuId,
          user_id: item.user,
        }) as [unknown, unknown]
    );

    const response = await Promise.all(promiseArr);

    const isError = response.some((item: any) => item[1]);

    if (isError) {
      SetErrorMessage(controller, { ...params, error: response.map((item) => item[1]) });

      return;
    }
  }

  // อัพเดท division has user records
  if (updatedItems.length > 0) {
    const promiseArr = updatedItems.map(
      (item) =>
        DivisionHasUserBulkUpdate.patch({
          apiToken: controller.apiToken,
          data: { extra: item.extra },
          params: { id__in: item.ids.join(",") },
        }) as [unknown, unknown]
    );

    const response = await Promise.all(promiseArr);

    const isError = response.some((item: any) => item[1]);

    if (isError) {
      SetErrorMessage(controller, { ...params, error: response.map((item) => item[1]) });

      return;
    }
  }

  const groupedItems = await GetDivisionHasUserGroupList(controller, {
    flattedDivision: seq.divisionOrganizations?.flatted ?? [],
    userId,
  });

  const dict = Object.fromEntries(
    groupedItems.map((item) => [item.rowIndex, seq.divisionOrganizations?.grouped ?? []])
  );

  controller.setState({
    DSPermissionSequence: {
      ...state.DSPermissionSequence,
      affiliateDict: dict,
      patientOpDivisions: groupedItems,
      roles: userRes?.roles || [],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.SUCCESS },
  });
};

const HandleParentSearch: Handler<Params<Actions["AFFILIATE_SEARCH"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const seq = state.DSPermissionSequence ?? {};
  const patientOpDivisions = seq.patientOpDivisions ?? [];
  const affiliateDict = seq.affiliateDict ?? {};

  const items = searchDivisionsAsTree(seq.divisionOrganizations?.flatted ?? [], params.search, [
    ORGANIZE_LABEL,
  ]);

  affiliateDict[params.id] = items;

  if ("search" in params) {
    const foundIndex = patientOpDivisions.findIndex((item) => item.rowIndex === params.id);

    if (foundIndex !== -1) {
      patientOpDivisions[foundIndex] = {
        ...patientOpDivisions[foundIndex],
        divisions: patientOpDivisions[foundIndex].divisions.map((div) => ({
          ...div,
          active: false,
        })),
        parentId: null,
        parentName: "",
      };
    }

    controller.setState({
      DSPermissionSequence: {
        ...state.DSPermissionSequence,
        affiliateDict: { ...affiliateDict },
        patientOpDivisions: [...patientOpDivisions],
      },
    });

    return;
  }

  controller.setState({ DSPermissionSequence: { ...seq, affiliateDict: { ...affiliateDict } } });
};

const HandleRemove: Handler<Params<Actions["REMOVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REMOVE]: BLC_STATUS.LOADING },
  });

  const { index } = params;

  const items = state.DSPermissionSequence?.patientOpDivisions ?? [];
  const userEmployee = state.selectedEmployee?.user_employee?.[0];
  const userId = userEmployee?.id;
  const deletedItems = items[index].divisions.filter((div) => div.dhuId);

  const promiseArr = deletedItems.map(
    (item) =>
      DivisionHasUserDetailForOtherUser.delete({
        apiToken: controller.apiToken,
        pk: item.dhuId,
        user_id: userId,
      }) as [unknown, unknown]
  );

  const response = await Promise.all(promiseArr);

  const isError = response.some((item: any) => item[1]);

  if (isError) {
    SetErrorMessage(controller, { ...params, error: response.map((item) => item[1]) });

    return;
  }

  items.splice(index, 1);

  controller.setState({
    DSPermissionSequence: { ...state.DSPermissionSequence, patientOpDivisions: [...items] },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REMOVE]: null },
  });

  params.onSuccess();
};

const HandleAdd: Handler<Params<Actions["ADD"]>> = async (controller) => {
  const state = controller.getState();

  const seq = state.DSPermissionSequence ?? {};
  const items = seq.patientOpDivisions ?? [];
  const affiliateDict = seq.affiliateDict ?? {};
  const maxRowIndex = Math.max(...items.map((item) => item.rowIndex));
  const rowIndex = Number.isInteger(maxRowIndex) ? maxRowIndex + 1 : 0;

  affiliateDict[rowIndex] = seq.divisionOrganizations?.grouped ?? [];

  items.push({
    adminMenuList: [],
    divisions: [],
    is_Active: true,
    isAccessPatient: true,
    rowIndex,
  });

  controller.setState({
    DSPermissionSequence: { ...seq, affiliateDict, patientOpDivisions: [...items] },
  });
};

const HandleChangeDivision: Handler<Params<Actions["CHANGE_DIVISION"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const { index, value } = params;
  const items = state.DSPermissionSequence?.patientOpDivisions ?? [];

  const divisions = items[index].divisions.map((div) => ({
    ...div,
    active: value.includes(div.id),
  }));
  const divisionIds = new Set(divisions.map((div) => div.id));

  const addedDivisions = value
    .filter((id) => !divisionIds.has(id))
    .map((id) => ({ id, active: true }));

  divisions.push(...addedDivisions);

  items[index].divisions = divisions;

  controller.setState({
    DSPermissionSequence: { ...state.DSPermissionSequence, patientOpDivisions: [...items] },
  });
};

const HandleChangeTreeView: Handler<Params<Actions["CHANGE_TREE_VIEW"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const { index, value } = params;
  const items = state.DSPermissionSequence?.patientOpDivisions ?? [];

  const parentName = getDivisionParentName(value, controller.data.allDivisionGroup ?? []);

  items[index].parentId = value ? Number(value) : null;
  items[index].parentName = parentName;
  items[index].divisions = items[index].divisions.map((div) => ({ ...div, active: false }));

  controller.setState({
    DSPermissionSequence: { ...state.DSPermissionSequence, patientOpDivisions: [...items] },
  });
};

const HandleChangeValue: Handler<Params<Actions["CHANGE_VALUE"]>> = async (controller, params) => {
  const state = controller.getState();

  const { index, name, value } = params;
  const items: any[] = state.DSPermissionSequence?.patientOpDivisions ?? [];

  items[index][name || ""] = value;

  controller.setState({
    DSPermissionSequence: { ...state.DSPermissionSequence, patientOpDivisions: [...items] },
  });
};

/** ====================================================  */
/**                         APIS                          */
/** ====================================================  */
const GetDivisionHasUserGroupList: Handler<
  {
    flattedDivision: Record<string, any>[];
    userId?: number;
  },
  Promise<PatientOpDivisionType[]>
> = async (controller, params) => {
  const [result] = await DivisionHasUserListForOtherUser.get({
    apiToken: controller.apiToken,
    pk: params.userId,
  });

  const dhuItems: Record<string, any>[] = result?.items || [];

  if (dhuItems.length > 0) {
    const grouped: Record<string, PatientOpDivisionType | undefined> = {};
    const usedRowIndices = new Set<number>();

    for (const [index, item] of dhuItems.entries()) {
      const extra = JSON.parse(item.extra);

      // หา rowIndex ที่ไม่ซ้ำ
      let rowIndex = extra.rowIndex ?? index;

      while (usedRowIndices.has(rowIndex)) {
        rowIndex++;
      }

      usedRowIndices.add(rowIndex);

      const key = extra.rowIndex ?? `row-${index}`;

      const adminMenuList: AdminMenuKeys[] = extra.adminMenuList || [];

      const parentName = getDivisionParentName(
        extra.parentId,
        controller.data.allDivisionGroup ?? []
      );

      grouped[key] ??= {
        adminMenuList: adminMenuList.filter((menu) => !Array.isArray(ADMIN_MENU_LIST[menu])),
        divisions: [],
        is_Active: extra.is_Active ?? false,
        isAccessPatient: extra.isAccessPatient ?? false,
        parentId: extra.parentId || null,
        parentName,
        rowIndex,
      };

      grouped[key]?.divisions.push({
        id: item.division.id,
        active: true,
        dhuId: item.id,
      });
    }

    return Object.values(grouped)
      .filter((value): value is PatientOpDivisionType => !!value)
      .sort((a, b) => a.rowIndex - b.rowIndex);
  }

  return [];
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */
export const formatMenuItems = (menuItems: string[]) => {
  const menuItemsWithCategories = menuItems.flatMap((menuItem) => {
    const menuCategoryEntry = Object.entries(ADMIN_MENU_LIST).find((categoryEntry) => {
      const value: string[] = Array.isArray(categoryEntry[1]) ? categoryEntry[1] : [];

      return value.includes(menuItem);
    });

    return menuCategoryEntry ? [menuCategoryEntry[0], menuItem] : [menuItem];
  });

  return [...new Set(menuItemsWithCategories)];
};

const formatDivisionHasUser = (items: PatientOpDivisionType[], userId?: number) =>
  items.flatMap((item) => {
    const activeDivisions = item.divisions.filter((div) => div.active || div.dhuId);

    return activeDivisions.map((div) => {
      const uniqueMenuItems = formatMenuItems(item.adminMenuList);

      const extraData = {
        ...div.extra,
        adminMenuList: uniqueMenuItems,
        is_Active: item.is_Active,
        isAccessPatient: item.isAccessPatient,
        parentId: item.parentId ?? null,
        rowIndex: item.rowIndex,
      };

      return {
        deleted: !div.active,
        dhuId: div.dhuId,
        division: div.id,
        ids: activeDivisions.filter((acc) => acc.dhuId).map((acc) => acc.dhuId),
        role: null,
        rowIndex: item.rowIndex,
        triage_level: "all",
        user: userId,
        extra: JSON.stringify(extraData),
      };
    });
  });
